import React, { useState, useEffect, useRef } from 'react';
import { useTheme, createThemedStyles } from '../../contexts/ThemeContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faCheck } from '@/icons';
import Select from 'react-select';
import type { BaseCalculatorProps } from './types';

const operationOptions = [
    { value: '%', label: '%' },
    { value: '+', label: '+' },
    { value: '-', label: '-' },
    { value: '*', label: '×' },
    { value: '/', label: '÷' },
];

export const BasicCalculator: React.FC<BaseCalculatorProps> = ({
    currentValue,
    decimalPlaces,
    onSave,
    onClose,
}) => {
    const theme = useTheme();
    const themedStyles = createThemedStyles(theme);
    const basicInput1Ref = useRef<HTMLInputElement>(null);
    const [basicInput1, setBasicInput1] = useState('');
    const [basicInput2, setBasicInput2] = useState('');
    const [basicOperation, setBasicOperation] = useState<'+' | '-' | '*' | '/' | '%'>('%');
    const [basicResult, setBasicResult] = useState(0);

    useEffect(() => {
        if (currentValue && !isNaN(parseFloat(currentValue)) && parseFloat(currentValue) > 0) {
            setBasicInput1(parseFloat(currentValue).toString());
            setBasicInput2('');
        } else {
            setBasicInput1('');
            setBasicInput2('');
        }
    }, [currentValue]);

    useEffect(() => {
        basicInput1Ref.current?.focus();
    }, []);

    useEffect(() => {
        const num1 = parseFloat(basicInput1) || 0;
        const num2 = parseFloat(basicInput2) || 0;
        let result = 0;
        switch (basicOperation) {
            case '+': result = num1 + num2; break;
            case '-': result = num1 - num2; break;
            case '*': result = num1 * num2; break;
            case '/': result = num2 !== 0 ? num1 / num2 : 0; break;
            case '%': result = num1 * (num2 / 100); break;
        }
        setBasicResult(result);
    }, [basicInput1, basicInput2, basicOperation]);

    const handleSave = () => {
        const rounded = Math.round(basicResult * Math.pow(10, decimalPlaces)) / Math.pow(10, decimalPlaces);
        onSave(rounded);
        onClose();
    };

    const selectStyles = {
        control: (provided: unknown, state: { isFocused: boolean }) => ({
            ...(provided as object),
            minHeight: '40px',
            border: `1px solid ${theme.colors.border.default}`,
            borderRadius: theme.borderRadius.sm,
            backgroundColor: theme.colors.background,
            ...(state?.isFocused && { borderColor: theme.colors.primary, boxShadow: `0 0 0 1px ${theme.colors.primary}` }),
        }),
        valueContainer: (provided: unknown) => ({ ...(provided as object), padding: '0 8px' }),
        indicatorSeparator: () => ({ display: 'none' }),
        indicatorsContainer: (provided: unknown) => ({ ...(provided as object), height: '40px' }),
        menu: (provided: unknown) => ({
            ...(provided as object),
            backgroundColor: theme.colors.background,
            border: `1px solid ${theme.colors.border.default}`,
            borderRadius: theme.borderRadius.sm,
            boxShadow: theme.shadows.md,
        }),
        option: (provided: unknown, state: { isSelected?: boolean; isFocused?: boolean }) => ({
            ...(provided as object),
            backgroundColor: state.isSelected ? theme.colors.primary : state.isFocused ? theme.colors.surfaceSecondary : theme.colors.background,
            color: state.isSelected ? theme.colors.text.inverse : theme.colors.text.primary,
        }),
        singleValue: (provided: unknown) => ({ ...(provided as object), color: theme.colors.text.primary }),
    };

    return (
        <>
            <div style={{
                backgroundColor: '#dbeafe',
                border: '1px solid #93c5fd',
                borderRadius: theme.borderRadius.sm,
                padding: theme.spacing.md,
                marginBottom: theme.spacing.lg,
                display: 'flex',
                alignItems: 'flex-start',
                gap: theme.spacing.sm,
            }}>
                <i className="fas fa-info-circle" style={{ color: '#1e40af', fontSize: theme.typography.fontSize.md, marginTop: '2px' }} />
                <p style={{ margin: 0, fontSize: theme.typography.fontSize.sm, color: '#1e40af', lineHeight: 1.5 }}>
                    Perform basic arithmetic operations. Use % for percentage calculations (e.g., 250000 * 20 % = 50,000).
                </p>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 2fr', gap: theme.spacing.md, alignItems: 'end', marginBottom: theme.spacing.lg }}>
                <div>
                    <label style={{ display: 'block', fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.bold, color: theme.colors.text.primary, marginBottom: theme.spacing.sm }}>First Number:</label>
                    <input
                        ref={basicInput1Ref}
                        type="number"
                        value={basicInput1}
                        onChange={(e) => setBasicInput1(e.target.value)}
                        style={{ width: '100%', padding: theme.spacing.sm, border: `1px solid ${theme.colors.border.default}`, borderRadius: theme.borderRadius.sm, fontSize: theme.typography.fontSize.sm }}
                        placeholder="0.00"
                        step="0.01"
                    />
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.bold, color: theme.colors.text.primary, marginBottom: theme.spacing.sm }}>Operation:</label>
                    <Select
                        value={operationOptions.find(opt => opt.value === basicOperation)}
                        onChange={(opt) => opt && setBasicOperation(opt.value as '+' | '-' | '*' | '/' | '%')}
                        options={operationOptions}
                        styles={selectStyles}
                        isSearchable={false}
                    />
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.bold, color: theme.colors.text.primary, marginBottom: theme.spacing.sm }}>Second Number:</label>
                    <input
                        type="number"
                        value={basicInput2}
                        onChange={(e) => setBasicInput2(e.target.value)}
                        style={{ width: '100%', padding: theme.spacing.sm, border: `1px solid ${theme.colors.border.default}`, borderRadius: theme.borderRadius.sm, fontSize: theme.typography.fontSize.sm }}
                        placeholder="0.00"
                        step="0.01"
                    />
                </div>
            </div>
            <div style={{ marginBottom: theme.spacing.xl }}>
                <label style={{ display: 'block', fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.bold, color: theme.colors.text.primary, marginBottom: theme.spacing.sm }}>Result:</label>
                <div style={{ padding: theme.spacing.sm, backgroundColor: theme.colors.surfaceSecondary, border: `1px solid ${theme.colors.border.default}`, borderRadius: theme.borderRadius.sm, fontSize: theme.typography.fontSize.md, fontWeight: theme.typography.fontWeight.medium, color: theme.colors.text.primary }}>
                    ${basicResult.toLocaleString('en-US', { minimumFractionDigits: decimalPlaces, maximumFractionDigits: decimalPlaces })}
                </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: theme.spacing.sm }}>
                <button onClick={onClose} style={{ ...themedStyles.button.primary, backgroundColor: 'transparent', color: theme.colors.text.primary, border: `1px solid ${theme.colors.border.default}`, padding: `${theme.spacing.sm} ${theme.spacing.lg}`, display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
                    <FontAwesomeIcon icon={faTimes} /> Cancel
                </button>
                <button onClick={handleSave} style={{ ...themedStyles.button.primary, backgroundColor: theme.colors.primary, color: theme.colors.text.inverse, padding: `${theme.spacing.sm} ${theme.spacing.lg}`, display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
                    <FontAwesomeIcon icon={faCheck} /> Apply
                </button>
            </div>
        </>
    );
};
